/**
 * React hook over the active `auth` client + `useUserStore`.
 *
 * Components should use this rather than touching `auth` or the store directly,
 * so the wiring (status transitions, store clearing on sign-out) lives in one
 * place.
 */
import { useCallback } from 'react'
import {
  auth,
  type AuthResult,
  type OAuthProvider,
  type SignUpInput,
  type SimpleResult,
} from '@/lib/auth/authClient'
import { useUserStore } from '@/stores/userStore'

export interface UseAuth {
  user: ReturnType<typeof useUserStore.getState>['user']
  status: ReturnType<typeof useUserStore.getState>['status']
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<AuthResult>
  signUp: (input: SignUpInput) => Promise<AuthResult>
  signInWithProvider: (provider: OAuthProvider) => Promise<AuthResult>
  resetPassword: (email: string) => Promise<SimpleResult>
  signOut: () => Promise<void>
}

export function useAuth(): UseAuth {
  const user = useUserStore((s) => s.user)
  const status = useUserStore((s) => s.status)

  const signIn = useCallback(async (email: string, password: string) => {
    return auth.signIn(email, password)
  }, [])

  const signUp = useCallback(async (input: SignUpInput) => {
    return auth.signUp(input)
  }, [])

  const signInWithProvider = useCallback(async (provider: OAuthProvider) => {
    return auth.signInWithProvider(provider)
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    return auth.resetPassword(email)
  }, [])

  const signOut = useCallback(async () => {
    await auth.signOut()
    // Authoritative store reset on sign-out, per the foundation contract.
    useUserStore.getState().clear()
  }, [])

  return {
    user,
    status,
    isAuthenticated: status === 'authenticated',
    signIn,
    signUp,
    signInWithProvider,
    resetPassword,
    signOut,
  }
}
